import { useEffect, useState } from 'react';
import { useLocation as useLocationPath, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, MapPin } from 'lucide-react';
import useSEO from '../hooks/useSEO';
import ListingCard from '../components/ListingCard';
import {
  CATEGORIES,
  CATEGORY_BY_BUY_SLUG,
  CATEGORY_BY_SELL_SLUG,
} from '../data/seoCategories';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = 'https://www.aerodeals.it';

/**
 * Unified landing page for category SEO pages (buy & sell).
 * Intent is inferred from the route: `mode` is either 'buy' or 'sell'.
 */
export default function CategoryLanding({ mode }) {
  const location = useLocationPath();
  const map = mode === 'buy' ? CATEGORY_BY_BUY_SLUG : CATEGORY_BY_SELL_SLUG;
  const slug = location.pathname.replace(/^\//, '').replace(/\/$/, '');
  const cat = map[slug];
  const [listings, setListings] = useState([]);

  // SEO title/description before the early-return check so the hook order stays stable
  const isBuy = mode === 'buy';
  const title = cat
    ? (isBuy ? `${cat.buyH1} — AeroDeals` : `${cat.sellH1} — AeroDeals`)
    : 'Pagina non trovata';
  const description = cat
    ? (isBuy
      ? `Annunci di ${cat.name.toLowerCase()} in vendita in Italia su AeroDeals. Ricerca per costruttore, anno, ore motore, prezzo e posizione. Annunci verificati da privati e operatori, messaggistica integrata e servizi di Lettera di Intenti e valutazione prezzo.`
      : `Come vendere un aeromobile di tipo ${cat.name.toLowerCase()} su AeroDeals: pubblicazione guidata, revisione manuale, messaggistica sicura, opzione In Evidenza, Lettera di Intenti e strumenti professionali per la compravendita.`)
    : '';
  const canonicalSlug = cat ? (isBuy ? cat.buySlug : cat.sellSlug) : slug;
  const jsonLd = cat ? {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE}/${canonicalSlug}`,
        url: `${SITE}/${canonicalSlug}`,
        name: isBuy ? cat.buyH1 : cat.sellH1,
        inLanguage: 'it-IT',
        primaryImageOfPage: { '@type': 'ImageObject', url: cat.image },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
            { '@type': 'ListItem', position: 2, name: isBuy ? cat.buyH1 : cat.sellH1, item: `${SITE}/${canonicalSlug}` },
          ],
        },
      },
    ],
  } : null;

  useSEO({
    title,
    description,
    image: cat?.image,
    canonical: cat ? `${SITE}/${canonicalSlug}` : undefined,
    keywords: cat ? `${cat.name.toLowerCase()}, aerei in vendita, compravendita ${cat.name.toLowerCase()}, ${isBuy ? 'comprare' : 'vendere'} ${cat.name.toLowerCase()}` : undefined,
    jsonLd,
  });

  useEffect(() => {
    if (!cat) return;
    axios.get(`${API}/listings`, { params: { category: cat.slug, limit: 6 } })
      .then(r => setListings(r.data.listings || []))
      .catch(() => setListings([]));
  }, [cat]);

  if (!cat) return <Navigate to="/" replace />;

  const primaryCta = isBuy
    ? { to: `/listings?category=${cat.slug}`, label: `Vedi tutti gli annunci ${cat.name.toLowerCase()}` }
    : { to: '/create-listing', label: `Pubblica un annuncio ${cat.name.toLowerCase()}` };
  const secondaryCta = isBuy
    ? { to: '/create-listing', label: 'Vendi il tuo aeromobile' }
    : { to: `/listings?category=${cat.slug}`, label: `Vedi gli altri ${cat.name.toLowerCase()} in vendita` };

  return (
    <div className="bg-black text-white min-h-screen" data-testid={`seo-${mode}-${cat.slug}`}>

      {/* Hero — uses SAME image as Home for this category */}
      <section className="relative pt-16 pb-20 overflow-hidden border-b border-zinc-900">
        <div className="absolute inset-0 opacity-[0.22] pointer-events-none">
          <img src={cat.image} alt="" className={`w-full h-full object-cover ${cat.imgPos || ''}`} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6">
          <nav className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#C5A059]">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-zinc-400">{isBuy ? 'Comprare' : 'Vendere'} {cat.name}</span>
          </nav>

          <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C5A059] mb-6">
            {isBuy ? 'Guida all\'acquisto' : 'Guida alla vendita'} · {cat.name}
          </p>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium mb-6 leading-[1.05] max-w-4xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {isBuy ? cat.buyH1 : cat.sellH1}
          </h1>

          <p className="text-base sm:text-lg text-zinc-300 leading-relaxed max-w-3xl mb-10">
            {cat.shortDesc} <span className="text-zinc-500">{cat.licenses}</span>
          </p>

          <div className="flex flex-wrap gap-3">
            <Link to={primaryCta.to} className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-7 py-3.5 text-sm font-medium uppercase tracking-wider hover:bg-[#D4B271] transition-colors" data-testid="cta-primary">
              {primaryCta.label} <ArrowRight size={16} />
            </Link>
            <Link to={secondaryCta.to} className="inline-flex items-center gap-2 border border-zinc-700 text-white px-7 py-3.5 text-sm font-medium uppercase tracking-wider hover:border-[#C5A059] hover:text-[#C5A059] transition-colors" data-testid="cta-secondary">
              {secondaryCta.label}
            </Link>
          </div>
        </div>
      </section>

      {/* SEO long-form content (600+ parole) */}
      <section className="py-20 border-b border-zinc-900">
        <div className="max-w-4xl mx-auto px-6">
          <article className="prose prose-invert prose-zinc max-w-none">
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">
              {isBuy ? 'Guida all\'acquisto' : 'Guida alla vendita'}
            </p>
            <h2 className="text-3xl sm:text-4xl text-white mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {isBuy ? `Come comprare un ${cat.name.toLowerCase()} su AeroDeals` : `Come vendere un ${cat.name.toLowerCase()} su AeroDeals`}
            </h2>

            <div className="text-base text-zinc-300 leading-[1.8] space-y-5 whitespace-pre-line">
              {isBuy ? cat.buyCopy : cat.sellCopy}
            </div>
          </article>
        </div>
      </section>

      {/* Current listings from DB (if any) — proof-of-stock for SEO */}
      {isBuy && listings.length > 0 && (
        <section className="py-20 border-b border-zinc-900">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-end justify-between mb-12 gap-4 flex-wrap">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Attualmente in Vendita</p>
                <h2 className="text-3xl sm:text-4xl text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {cat.name} disponibili ora
                </h2>
              </div>
              <Link to={`/listings?category=${cat.slug}`} className="text-xs font-mono uppercase tracking-wider text-[#C5A059] hover:text-white flex items-center gap-2">
                Vedi tutti <ArrowRight size={12} />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map(l => <ListingCard key={l.listing_id} listing={l} />)}
            </div>
          </div>
        </section>
      )}

      {/* Regional cross-links */}
      <section className="py-20 border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Altre categorie</p>
          <h2 className="text-3xl sm:text-4xl text-white mb-10" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {isBuy ? 'Scopri altre categorie di aeromobili' : 'Vuoi vendere un altro tipo di aeromobile?'}
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {CATEGORIES.filter(c => c.slug !== cat.slug).map(c => (
              <Link
                key={c.slug}
                to={`/${isBuy ? c.buySlug : c.sellSlug}`}
                className="flex items-center gap-3 p-4 border border-zinc-800 hover:border-[#C5A059]/40 transition-colors group"
                data-testid={`cross-${c.slug}`}
              >
                <div className="w-14 h-14 overflow-hidden bg-zinc-900 shrink-0">
                  <img src={c.image} alt={c.name} className={`w-full h-full object-cover ${c.imgPos || ''}`} loading="lazy" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-white font-medium truncate group-hover:text-[#C5A059] transition-colors">{c.name}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{isBuy ? 'Comprare' : 'Vendere'}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA — redirect to main marketplace */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl text-white mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {isBuy ? `Pronto a scegliere il tuo ${cat.name.toLowerCase().replace(/s$/, '')}?` : `Pronto a vendere?`}
          </h2>
          <p className="text-zinc-400 text-base leading-relaxed max-w-2xl mx-auto mb-8">
            {isBuy
              ? `Esplora tutti gli annunci di ${cat.name.toLowerCase()} in Italia su AeroDeals. Filtri avanzati per costruttore, anno, ore e posizione, e strumenti di compravendita dedicati all'aviazione.`
              : `Pubblica il tuo annuncio in pochi minuti e raggiungi una community qualificata di acquirenti in tutta Italia ed Europa.`}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to={primaryCta.to} className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-8 py-4 text-sm font-medium uppercase tracking-wider hover:bg-[#D4B271] transition-colors">
              {primaryCta.label} <ArrowRight size={16} />
            </Link>
            <Link to="/listings" className="inline-flex items-center gap-2 border border-zinc-700 text-white px-8 py-4 text-sm font-medium uppercase tracking-wider hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
              Tutti gli annunci <MapPin size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
